import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { JimboResponse, AvaSuburbStatus, AvaSuburbHours, AvaSuburbs, ServiceStatus, AvaStore, AvsStoreSkuMap } from '@models';

export const getSuburb = (
  builder: EndpointBuilder<
    // eslint-disable-next-line @typescript-eslint/ban-types
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    'Suburb' | 'Service' | 'Store' | 'Inventory',
    'avaApi'
  >,
) => {
  return {
    getSuburbStatus: builder.query<AvaSuburbStatus, { suburb: string }>({
      query: (query) => {
        console.log(process.env.REACT_APP_API_URL);
        return `ava/suburb-status?suburb=${query.suburb}`;
      },
      providesTags: ['Suburb'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaSuburbStatus>) => response.data!,
    }),
    getSuburbHours: builder.query<AvaSuburbHours, { suburb: string }>({
      query: (query) => `ava/suburb-hours?suburb=${query.suburb}`,
      providesTags: ['Suburb'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaSuburbHours>) => response.data!,
    }),
    getSuburbsList: builder.query<AvaSuburbs[], void>({
      query: () => 'ava/suburbslist',
      providesTags: ['Suburb'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaSuburbs[]>) => response.data!,
    }),
    getSuburb: builder.query<AvaSuburbs, { suburb: string }>({
      query: (query) => `ava/suburb?suburb=${query.suburb}&t=${new Date().getTime()}`,
      providesTags: ['Suburb'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaSuburbs>) => response.data!,
    }),
    getSuburbNoCache: builder.query<AvaSuburbs, { suburb: string }>({
      query: (query) => `ava/suburb-no-cache?suburb=${query.suburb}`,
      providesTags: ['Suburb'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaSuburbs>) => response.data!,
    }),
  };
};

export const avaApi = createApi({
  reducerPath: 'avaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers) => {
      headers.set('Authorization', `Bearer ${localStorage.getItem('idToken')}`);
      return headers;
    },
  }),
  tagTypes: ['Suburb', 'Service', 'Store', 'Inventory'],
  endpoints: (builder) => ({
    ...getSuburb(builder),
    getAllServices: builder.query<ServiceStatus[], void>({
      query: () => 'ava/services',
      providesTags: ['Service'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<ServiceStatus[]>) => response.data!,
    }),
    updateService: builder.mutation<ServiceStatus, Partial<ServiceStatus>>({
      query: (service) => ({
        url: `ava/services`,
        method: 'POST',
        body: service,
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<ServiceStatus>) => response.data!,
      invalidatesTags: ['Service'],
    }),
    updateSuburb: builder.mutation<string, Partial<AvaSuburbs>[]>({
      query: (suburbs) => ({
        url: `ava/suburb`,
        method: 'POST',
        body: suburbs,
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<string>) => response.data!,
      invalidatesTags: ['Suburb'],
    }),
    getStoresList: builder.query<AvaStore[], void>({
      query: () => 'ava/storeslist',
      providesTags: ['Store'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaStore[]>) => response.data!,
    }),
    getStore: builder.query<AvaStore[], { store: string }>({
      query: (query) => `ava/store?store=${query.store}&t=${new Date().getTime()}`, //this is a temp solution. Will fix the backend cache issue
      providesTags: ['Store'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaStore[]>) => response.data!,
    }),
    updateStore: builder.mutation<string, Partial<AvaStore>>({
      query: (store) => ({
        url: `ava/store`,
        method: 'PUT',
        body: store,
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<string>) => response.data!,
      invalidatesTags: ['Store'],
    }),
    updateStoresList: builder.mutation<string, Partial<AvaStore[]>>({
      query: (stores) => ({
        url: `ava/store`,
        method: 'POST',
        body: stores,
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<string>) => response.data!,
      invalidatesTags: ['Store'],
    }),
    getStoresListNoCache: builder.query<AvaStore[], void>({
      query: () => 'ava/stores-no-cache',
      providesTags: ['Store'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaStore[]>) => response.data!,
    }),
    getStoreNoCache: builder.query<AvaStore[], { store: string }>({
      query: (query) => `ava/store?store=${query.store}/no-cache`,
      providesTags: ['Store'],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<AvaStore[]>) => response.data!,
    }),
    updateInventoryStatus: builder.mutation<string, Partial<AvsStoreSkuMap>>({
      query: (stores) => ({
        url: `ava/inventory/status`,
        method: 'POST',
        body: stores,
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transformResponse: (response: JimboResponse<string>) => response.data!,
      invalidatesTags: ['Inventory'],
    }),
  }),
});

export const {
  useGetAllServicesQuery,
  useUpdateServiceMutation,
  useGetSuburbHoursQuery,
  useGetSuburbStatusQuery,
  useGetSuburbsListQuery,
  useUpdateSuburbMutation,
  useGetSuburbQuery,
  useGetStoresListQuery,
  useGetStoreQuery,
  useGetSuburbNoCacheQuery,
  useUpdateStoreMutation,
  useUpdateStoresListMutation,
  useUpdateInventoryStatusMutation,
  useGetStoresListNoCacheQuery,
  useGetStoreNoCacheQuery,
} = avaApi;
